import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import MuiTextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ListItemText } from 'common/components/ListItem';
import { IGroupConditionInput, TGroupListItem } from 'user/types';

interface IProps {
    groupConditionInput: IGroupConditionInput;
    groupConditionInputIndex: number;
    group: TGroupListItem;
    showMinMaxFilters: boolean;
    onUpdate: (index: number, value: IGroupConditionInput) => void;
}

export const GroupConditionGroupExtendedListItem = ({
    groupConditionInput,
    groupConditionInputIndex,
    group,
    showMinMaxFilters,
    onUpdate,
}: IProps) => {
    const [translate] = useTranslation();
    const { id: groupId, name: groupName } = group;

    // Find the minMaxFilter for this group by groupId (not positional index)
    const currentMinMaxFilter = useMemo(
        () =>
            groupConditionInput.minMaxFilters?.find(
                (filter) => filter.groupId === groupId
            ),
        [groupConditionInput.minMaxFilters, groupId]
    );

    const handleChangeMinMax = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const { name, value } = e.target;

            // Allow empty string (clearing the field) or valid non-negative integers only
            if (
                value !== '' &&
                (value.trim() === '' ||
                    isNaN(+value) ||
                    !Number.isInteger(+value) ||
                    +value < 0)
            )
                return;

            const numValue = value === '' ? null : +value;
            const isMin = name === 'min';
            const newMinMaxFilters = [
                ...(groupConditionInput.minMaxFilters || []),
            ];

            const filterIndex = newMinMaxFilters.findIndex(
                (f) => f.groupId === groupId
            );

            if (filterIndex === -1) {
                newMinMaxFilters.push({
                    groupId,
                    min: isMin ? numValue : null,
                    max: isMin ? null : numValue,
                });
            } else {
                newMinMaxFilters[filterIndex] = {
                    ...newMinMaxFilters[filterIndex],
                    min: isMin ? numValue : newMinMaxFilters[filterIndex].min,
                    max: isMin ? newMinMaxFilters[filterIndex].max : numValue,
                };
            }

            onUpdate(groupConditionInputIndex, {
                ...groupConditionInput,
                minMaxFilters: newMinMaxFilters,
            });
        },
        [groupConditionInput, groupConditionInputIndex, groupId, onUpdate]
    );

    return (
        <ListItem alignItems="flex-start" sx={{ p: 0.5 }}>
            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                }}
            >
                <ListItemText>{groupName}</ListItemText>

                {showMinMaxFilters && (
                    <Stack direction="row" spacing={2} sx={{ ml: 'auto' }}>
                        <MuiTextField
                            label={translate('groupConditionFilter.min')}
                            margin="normal"
                            name="min"
                            placeholder="0"
                            slotProps={{
                                htmlInput: {
                                    sx: { width: 40, height: 7 },
                                },
                                inputLabel: { shrink: true },
                            }}
                            sx={{
                                '.MuiOutlinedInput-input': { py: 2 },
                            }}
                            value={currentMinMaxFilter?.min ?? ''}
                            variant="outlined"
                            onChange={handleChangeMinMax}
                        />

                        <MuiTextField
                            label={translate('groupConditionFilter.max')}
                            margin="normal"
                            name="max"
                            placeholder="0"
                            slotProps={{
                                htmlInput: {
                                    sx: { width: 40, height: 7 },
                                },
                                inputLabel: { shrink: true },
                            }}
                            sx={{
                                '.MuiOutlinedInput-input': { py: 2 },
                            }}
                            value={currentMinMaxFilter?.max ?? ''}
                            variant="outlined"
                            onChange={handleChangeMinMax}
                        />
                    </Stack>
                )}
            </Box>
        </ListItem>
    );
};
